import useInnerWidth from '@/hooks/useInnerWidth'
import useOnClickOutside from '@/hooks/useOnClickOutside'
import DownIcons from '@/icons/DownIcons'
import { SidebarType } from '@/types/shared'
import { memo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Collapse, MenuItem } from 'vnetwork-ui'
import withPermission from '@/hocs/withPermission'
import usePermissionsStore from '@/stores/usePermissionsStore'

interface ItemProps {
    sidebar: SidebarType
    size?: 'small' | 'medium' | 'large'
    hiddenText?: boolean
}

// Generate data-tour attribute from path
const getTourId = (path: string) => {
    if (!path) return undefined
    // Convert path to tour id: /dayoff-account -> sidebar-dayoff-account
    const cleanPath = path.replace(/^\//, '').replace(/\//g, '-')
    return `sidebar-${cleanPath}`
}

// Component for child menu items with permission
const ChildMenuItem: React.FC<{
    child: SidebarType
    size: 'small' | 'medium' | 'large'
    pathname: string
    t: any
}> = memo(({ child, size, pathname, t }) => {
    const tourId = getTourId(child?.path)
    const { isAdmin } = usePermissionsStore()

    // Hide super_admin-only items from non-admin users
    if (child?.require_admin && !isAdmin) {
        return null
    }

    const childHasResource =
        !!child.rsname || !!(child.rsnames && child.rsnames.length > 0)
    if (!childHasResource || !child.require_scopes) {
        // External link — open in new tab
        if (child.external) {
            return (
                <a
                    key={child?.path}
                    href={child?.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-tour={tourId}
                >
                    <MenuItem
                        color="info"
                        key={child?.path}
                        value={child?.path}
                        children={t(child?.title)}
                        icon={child?.icon}
                        selected={false}
                        className={`rounded-[8px] pl-10 md:justify-start justify-center text-gray-5f text-sm capitalize`}
                        size={size}
                    />
                </a>
            )
        }
        // Fallback for items without permission config
        return (
            <Link key={child?.path} to={child?.path} data-tour={tourId}>
                <MenuItem
                    color="info"
                    key={child?.path}
                    value={child?.path}
                    children={t(child?.title)}
                    icon={child?.icon}
                    selected={pathname === child?.path}
                    className={`rounded-[8px] pl-10 md:justify-start justify-center text-gray-5f text-sm capitalize`}
                    size={size}
                />
            </Link>
        )
    }

    const ChildMenuItemWithPermission = withPermission(
        () => (
            <Link key={child?.path} to={child?.path} data-tour={tourId}>
                <MenuItem
                    color="info"
                    key={child?.path}
                    value={child?.path}
                    children={t(child?.title)}
                    icon={child?.icon}
                    selected={pathname === child?.path}
                    className={`rounded-[8px] pl-10 md:justify-start justify-center text-gray-5f text-sm capitalize`}
                    size={size}
                />
            </Link>
        ),
        {
            rsname: child.rsname,
            rsnames: child.rsnames,
            require_scopes: child.require_scopes,
            visible_permission_denied: child.visible_permission_denied ?? false,
            useInSidebar: true,
        }
    )

    return <ChildMenuItemWithPermission />
})

const Item: React.FC<ItemProps> = ({ sidebar, size = 'small', hiddenText }) => {
    const { pathname } = useLocation()

    const { t } = useTranslation()

    const childMenuRef = useRef<HTMLDivElement>(null)

    const [showChildren, setShowChildren] = useState(false)
    const { isMobile } = useInnerWidth()

    const handleClickMenu = () => {
        if (sidebar?.children && sidebar?.children?.length > 0) {
            setShowChildren(!showChildren)
            return
        }
    }

    useOnClickOutside(childMenuRef, () => setShowChildren(false))

    const isExternal = sidebar?.external
    const WrapComponent =
        sidebar?.children && sidebar?.children?.length > 0
            ? 'div'
            : isExternal
              ? 'a'
              : Link

    // Generate tour id for parent menu
    const parentTourId = sidebar?.title
        ? `sidebar-parent-${sidebar.title.toLowerCase().replace(/\s+/g, '-')}`
        : undefined

    const wrapProps = isExternal
        ? { href: sidebar?.path, target: '_blank', rel: 'noopener noreferrer' }
        : { to: sidebar?.path, onClick: handleClickMenu }

    return (
        <div ref={childMenuRef} data-tour={parentTourId}>
            <div className="relative">
                <WrapComponent {...(wrapProps as any)}>
                    <MenuItem
                        color="info"
                        value={sidebar?.path}
                        children={
                            !hiddenText ? null : (
                                <div className="flex-1 line-clamp-1">
                                    {t(sidebar?.title)}
                                </div>
                            )
                        }
                        icon={sidebar?.icon}
                        selected={pathname === sidebar?.path}
                        className={`rounded-[8px] w-full ${!hiddenText ? 'items-center justify-center' : ''} text-sm capitalize text-gray-5f`}
                        size={isMobile ? 'large' : size}
                    />
                </WrapComponent>

                {sidebar?.children &&
                    sidebar?.children?.length > 0 &&
                    hiddenText && (
                        <DownIcons
                            onClick={handleClickMenu}
                            className={`icon-${size} ${showChildren && 'rotate-180'} transition-all cursor-pointer duration-300 absolute top-[50%] right-4 translate-y-[-50%]`}
                        />
                    )}
            </div>

            <div>
                <Collapse
                    open={Boolean(
                        sidebar?.children &&
                            sidebar?.children?.length > 0 &&
                            showChildren
                    )}
                    className={
                        !hiddenText
                            ? 'fixed z-[9999] md:left-auto md:right-auto left-0 right-0 min-w-[200px] bottom-[59px] md:bottom-auto rounded-[8px] bg-white shadow-md'
                            : 'static'
                    }
                >
                    {sidebar?.children?.map((child) => (
                        <ChildMenuItem
                            key={child?.path}
                            child={child}
                            size={size}
                            pathname={pathname}
                            t={t}
                        />
                    ))}
                </Collapse>
            </div>
        </div>
    )
}

// Main component with permission wrapper
const ItemWithPermission: React.FC<ItemProps> = ({
    sidebar,
    size = 'small',
    hiddenText,
}) => {
    const { permissions, isAdmin } = usePermissionsStore()

    // Hide super_admin-only items from non-admin users
    if (sidebar?.require_admin && !isAdmin) {
        return null
    }

    if (sidebar?.ignorePermission) {
        // Fallback for items without permission config
        return <Item sidebar={sidebar} size={size} hiddenText={hiddenText} />
    }

    if (sidebar?.children) {
        // Check if at least one child menu has permission access
        const hasAnyChildWithAccess = sidebar.children.some((child) => {
            const childResources =
                child.rsnames && child.rsnames.length > 0
                    ? child.rsnames
                    : child.rsname
                      ? [child.rsname]
                      : []
            if (childResources.length === 0 || !child.require_scopes) {
                return true // Items without permission config are accessible
            }

            // Granted if ANY candidate resource satisfies every required scope (OR across resources).
            const hasRequiredScopes = childResources.some((res) => {
                const scopes =
                    permissions.find((permission) => permission?.rsname === res)
                        ?.scopes || []
                return child.require_scopes!.every((rq_scope) =>
                    scopes.includes(rq_scope)
                )
            })

            return hasRequiredScopes || isAdmin
        })

        if (hasAnyChildWithAccess) {
            // có ít nhất 1 child menu có quyền access
            return (
                <Item sidebar={sidebar} size={size} hiddenText={hiddenText} />
            )
        } else {
            // nếu tất cả các child menu đều không có quyền access
            return null
        }
    }

    const ComponentWithPermission = withPermission(
        () => <Item sidebar={sidebar} size={size} hiddenText={hiddenText} />,
        {
            rsname: sidebar.rsname,
            // rsnames: sidebar.rsnames,
            require_scopes: sidebar.require_scopes || [],
            visible_permission_denied:
                sidebar.visible_permission_denied ?? false,
            useInSidebar: true,
        }
    )

    return <ComponentWithPermission />
}

export default memo(ItemWithPermission)
